// Project-Organization association
export interface ProjectOrganizationDefaults {
  defaultSharingLevel?: string;
  defaultSharingLevelUnavailable?: boolean;
  defaultAttribution?: string;
}

export interface ProjectOrganizationDefaultsUpdate {
  defaultSharingLevel?: string | null;
  defaultAttribution?: string | null;
}

export async function addProjectOrganization(token: string, projectId: string, orgId: string, defaults?: ProjectOrganizationDefaults): Promise<void> {
  const res = await fetch(`${API}/projects/${projectId}/organizations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      organization_id: orgId,
      ...(defaults?.defaultSharingLevel ? { default_sharing_level: defaults.defaultSharingLevel } : {}),
      ...(defaults?.defaultAttribution ? { default_attribution: defaults.defaultAttribution } : {}),
    }),
  });
  if (!res.ok) {
    // Try to extract detailed error message from response
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
}

export async function removeProjectOrganization(token: string, projectId: string, orgId: string): Promise<void> {
  const res = await fetch(`${API}/projects/${projectId}/organizations/${orgId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
}

export async function updateProjectOrganization(token: string, projectId: string, orgId: string, defaults: ProjectOrganizationDefaultsUpdate): Promise<void> {
  const body: {
    organization_id: string;
    default_sharing_level?: string | null;
    default_attribution?: string | null;
  } = { organization_id: orgId };
  if ("defaultSharingLevel" in defaults) body.default_sharing_level = defaults.defaultSharingLevel;
  if ("defaultAttribution" in defaults) body.default_attribution = defaults.defaultAttribution;

  const res = await fetch(`${API}/projects/${projectId}/organizations/${orgId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // use status text
    }
    throw new Error(errorMessage);
  }
}

// Project-User association
export async function addProjectUser(token: string, projectId: string, userId: string, role: string, organizationId?: string): Promise<void> {
  const res = await fetch(`${API}/projects/${projectId}/users`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: userId,
      role,
      ...(organizationId && { organization_id: organizationId }),
    }),
  });
  if (!res.ok) {
    // Try to extract detailed error message from response
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
}

export async function removeProjectUser(token: string, projectId: string, userId: string, organizationId?: string): Promise<void> {
  let url = `${API}/projects/${projectId}/users/${userId}`;
  if (organizationId) {
    url += `?organization_id=${encodeURIComponent(organizationId)}`;
  }
  const res = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
}

// Project Shares
export interface ProjectShare {
  project_id: string;
  from_organization_id: string;
  to_organization_id: string;
  role: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ListProjectSharesResponse {
  shares: ProjectShare[];
  total: number;
}

export async function listAllProjectShares(token: string, projectId: string): Promise<ProjectShare[]> {
  const res = await fetch(`${API}/projects/${projectId}/shares`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data: ListProjectSharesResponse = await res.json();
  return data.shares || [];
}

export async function createProjectShare(
  token: string,
  projectId: string,
  fromOrganizationId: string,
  toOrganizationId: string,
  role: string,
): Promise<ProjectShare> {
  const res = await fetch(`${API}/projects/${projectId}/shares`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      from_organization_id: fromOrganizationId,
      to_organization_id: toOrganizationId,
      role,
    }),
  });
  if (!res.ok) {
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
  return res.json();
}

export async function deleteProjectShare(token: string, projectId: string, fromOrganizationId: string, toOrganizationId: string): Promise<void> {
  const res = await fetch(
    `${API}/projects/${projectId}/shares?from_organization_id=${encodeURIComponent(fromOrganizationId)}&to_organization_id=${encodeURIComponent(
      toOrganizationId,
    )}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
}

// Central API file for all backend resources (projects, organizations, users, etc.)
// Only include types and functions actually used in the app.

export interface OrganizationSummary {
  id: string;
  name: string;
  blobContainerName: string;
  defaultSharingLevel?: string;
  defaultSharingLevelUnavailable?: boolean;
  defaultAttribution?: string;
}

export interface UserSummary {
  id: string;
  name: string;
  email?: string;
  role?: string;
  organization_id?: string;
  organization_name?: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  project_date?: string;
  status?: string;
  collectionId?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Not returned by the list endpoint — present only in request payloads (createProject/updateProject). */
  organizationIds?: string[];
  /** Not returned by the list endpoint — present only in request payloads (createProject/updateProject). */
  userIds?: string[];
}

export interface ProjectDetails {
  id: string;
  name: string;
  description?: string;
  project_date?: string;
  status?: string;
  collectionId?: string;
  createdAt: string;
  updatedAt: string;
  organizations: OrganizationSummary[];
  users: UserSummary[];
}

export interface Organization {
  id: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserOrganizationInfo {
  organizationId: string;
  organizationName: string;
  role?: string;
}

export interface User {
  id: string;
  name: string;
  email?: string;
  /** Only present when fetching a single user (GET /users/{id}); absent from the list endpoint. */
  isOwner?: boolean;
  organizationId: string; // Primary/first organization (for backward compatibility)
  organizations?: UserOrganizationInfo[]; // All organizations the user belongs to
  createdAt?: string;
  updatedAt?: string;
}

const API = import.meta.env.FRONTEND_API_BASE_URL ?? "";

export interface AccessUsageSums {
  request_count: number;
  bytes_out: number;
  bytes_in: number;
  egress_cost: string;
  retrieval_cost: string;
}

export interface AccessUsageRow extends AccessUsageSums {
  keys: (string | null)[];
  names: (string | null)[];
}

export interface AccessUsageReport {
  currency: string;
  start: string;
  end: string;
  group_by: string[];
  rows: AccessUsageRow[];
  total: AccessUsageSums;
}

export async function getMyAccessUsage(token: string, userId: string, start: string, end: string): Promise<AccessUsageReport> {
  const params = new URLSearchParams({ from: start, to: end, group_by: "day,project" });
  const res = await fetch(`${API}/users/${encodeURIComponent(userId)}/access?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export interface StorageUsageReport {
  currency: string;
  start: string;
  end: string;
  group_by: string[];
  rows: { keys: (string | null)[]; names: (string | null)[]; storage_cost: string; latest_day: string }[];
  total_storage_cost: string;
}

export async function getProjectAccessCosts(token: string, start: string, end: string): Promise<AccessUsageReport> {
  const params = new URLSearchParams({ from: start, to: end, group_by: "project" });
  const res = await fetch(`${API}/usage/access?${params}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export async function getProjectStorageCosts(token: string, start: string, end: string): Promise<StorageUsageReport> {
  const params = new URLSearchParams({ from: start, to: end, group_by: "project" });
  const res = await fetch(`${API}/usage/storage?${params}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export interface ProjectMutationBody {
  name: string;
  description: string;
  organizationIds: string[];
  userIds: string[];
  project_date?: string;
}

async function getErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;
    if (typeof data?.error === "string") return data.error;
  } catch {
    // Ignore JSON parsing failures and fall through to text/status handling.
  }

  try {
    const text = await res.text();
    if (text) return text;
  } catch {
    // Ignore text parsing failures and use the fallback below.
  }

  return fallback;
}

// Helper for authenticated GET requests
// async function fetchJSON<T>(url: string, token: string): Promise<T> {
//   const res = await fetch(url, {
//     headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
//   });
//   if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
//   return res.json() as Promise<T>;
// }

// --------------------------------------------------------------
// Projects
// --------------------------------------------------------------
export async function listAllProjects(token: string): Promise<Project[]> {
  const res = await fetch(`${API}/projects?limit=0`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  // Return empty array for 403 (not admin) instead of throwing
  if (res.status === 403) return [];
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.projects || [];
}

export async function getProject(token: string, projectId: string): Promise<ProjectDetails> {
  const res = await fetch(`${API}/projects/${projectId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export async function createProject(token: string, body: ProjectMutationBody): Promise<Project> {
  const res = await fetch(`${API}/projects`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await getErrorMessage(res, `${res.status} ${res.statusText}`));
  return (await res.json()) as Project;
}

export async function updateProject(token: string, id: string, body: ProjectMutationBody): Promise<Project> {
  const res = await fetch(`${API}/projects/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await getErrorMessage(res, `${res.status} ${res.statusText}`));
  return (await res.json()) as Project;
}

export async function renameProject(token: string, id: string, name: string): Promise<Project> {
  const res = await fetch(`${API}/projects/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await getErrorMessage(res, `${res.status} ${res.statusText}`));
  return (await res.json()) as Project;
}

// --------------------------------------------------------------
// Organizations
// --------------------------------------------------------------
export async function listAllOrganizations(token: string): Promise<Organization[]> {
  const res = await fetch(`${API}/organizations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  // Return empty array for 403 (not admin) instead of throwing
  if (res.status === 403) return [];
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.organizations || [];
}

export async function createOrganization(token: string, name: string, apiBase: string = API): Promise<Organization> {
  const res = await fetch(`${apiBase}/organizations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await getErrorMessage(res, `HTTP ${res.status}`));
  return (await res.json()) as Organization;
}

export async function deleteOrganization(token: string, id: string): Promise<void> {
  const res = await fetch(`${API}/organizations/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
}

export async function renameOrganization(token: string, id: string, name: string): Promise<Organization> {
  const res = await fetch(`${API}/organizations/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await getErrorMessage(res, `${res.status} ${res.statusText}`));
  return (await res.json()) as Organization;
}

// --------------------------------------------------------------
// Users
// --------------------------------------------------------------
export async function listAllUsers(token: string): Promise<User[]> {
  const res = await fetch(`${API}/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  // Return empty array for 403 (not admin) instead of throwing
  if (res.status === 403) return [];
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.users || [];
}

export async function deleteUser(token: string, id: string): Promise<void> {
  const res = await fetch(`${API}/users/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
}

export interface UserProject {
  projectId: string;
  organizationId: string;
  role: string;
}

export interface UserWithProjects extends User {
  organizationId: string;
  projects: UserProject[];
}

export async function getUser(token: string, userId: string): Promise<UserWithProjects> {
  const res = await fetch(`${API}/users/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export interface UpdateUserData {
  name?: string;
  email?: string;
}

export async function updateUser(token: string, userId: string, data: UpdateUserData): Promise<User> {
  const res = await fetch(`${API}/users/${userId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    // Try to extract detailed error message from response
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
  return res.json();
}

export async function addUserToOrganization(token: string, userId: string, organizationId: string, role: string = "data_reader"): Promise<void> {
  const res = await fetch(`${API}/users/${userId}/organizations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ organizationId, role }),
  });
  if (!res.ok) {
    // Try to extract detailed error message from response
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
}

export async function removeUserFromOrganization(token: string, userId: string, organizationId: string): Promise<void> {
  const res = await fetch(`${API}/users/${userId}/organizations/${organizationId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    // Try to extract detailed error message from response
    let errorMessage = `${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the status text
    }
    throw new Error(errorMessage);
  }
}

// --------------------------------------------------------------
// STAC Collections
// --------------------------------------------------------------
export interface Collection {
  id: string;
  type: string; // always "Collection"
  stac_version: string;
  stac_extensions?: string[];
  title: string;
  description: string;
  project_date?: string;
  keywords: string[];
  license: string;
  providers: Record<string, unknown>[];
  extent?: {
    spatial: { bbox: [number, number, number, number][] };
    temporal: { interval: [string | null, string | null][] };
  };
  summaries?: Record<string, unknown>;
  links: Record<string, unknown>[];
  item_assets: Record<string, unknown>;
  project?: unknown; // adjust if you have a Project interface
}

export async function listCollections(token: string): Promise<Collection[]> {
  const res = await fetch(`${API}/stac/collections`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.collections || [];
}

// --------------------------------------------------------------
// STAC Items
// --------------------------------------------------------------
import type { Geometry } from "geojson";

export interface StacItem {
  id: string;
  type: string; // always "Feature"
  stac_version: string;
  stac_extensions?: string[];
  geometry?: Geometry;
  bbox?: number[];
  properties: {
    dataset_title?: string;
    title?: string;
    datetime?: string;
    published?: boolean;
    sharing_level?: string;
    attribution?: string;
    category?: string;
    description?: string;
    dataset_description?: string;
    data_category?: string;
    columns?: string[];
    bbox_wgs84?: number[];
    geometry_type?: string | null;
    layers?: Record<string, { geometry_type?: string | null; feature_count?: number; columns?: string[]; [key: string]: unknown }>;
    crs?: string;
    dtype?: string;
    tile_count?: number;
    /** Questionnaire answers keyed by question id, stored when harmonizing with a profile that has a questionnaire. */
    questionnaire_answers?: Record<string, string>;
    /** @deprecated Legacy field — now stored in questionnaire_answers */
    field_confirmation?: string;
    /** @deprecated Legacy field — now stored in questionnaire_answers */
    expert_verification?: string;
    /** @deprecated Legacy field — now stored in questionnaire_answers */
    positional_accuracy?: string;
    /** @deprecated Legacy field — now stored in questionnaire_answers */
    positioning_method?: string;
    [key: string]: unknown; // Allow additional properties
  };
  links?: Array<{ rel: string; href: string; [key: string]: unknown }>;
  assets: Record<string, { roles?: string[]; [key: string]: unknown }>;
  collection: string; // STAC spec uses 'collection', not 'collection_id'
  organization_id?: string; // Deprecated - backend returns 'organization'
  organization?: string;
  user_id?: string; // Deprecated - backend returns 'user'
  user?: string;
}

export interface StacItemCollectionResponse {
  features: StacItem[];
  links?: Record<string, unknown>[];
  numberMatched?: number;
  numberReturned?: number;
}

export async function searchStacItems(token: string, q: string, limit: number = 50, offset: number = 0): Promise<StacItemCollectionResponse> {
  const params = new URLSearchParams();
  params.append("q", q);
  params.append("limit", String(limit));
  if (offset) params.append("offset", String(offset));

  const res = await fetch(`${API}/stac/search?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export async function listCollectionItems(token: string, collectionId: string, includeUnpublished: boolean = false): Promise<StacItem[]> {
  const querySearchParams = new URLSearchParams();
  if (includeUnpublished) {
    querySearchParams.append("include_unpublished", "true");
  }
  let uri = `${API}/stac/collections/${collectionId}/items`;
  if (querySearchParams.toString()) {
    uri += `?${querySearchParams.toString()}`;
  }
  const res = await fetch(uri, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.features || [];
}

export async function getStacItem(token: string, collectionId: string, itemId: string, options: { signAssets?: boolean } = {}): Promise<StacItem> {
  const params = new URLSearchParams();
  if (options.signAssets) {
    params.append("sign", "true");
  }

  const qs = params.toString();
  const uri = `${API}/stac/collections/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(itemId)}${qs ? `?${qs}` : ""}`;

  const res = await fetch(uri, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export async function deleteItem(token: string, collectionId: string, itemId: string): Promise<void> {
  const res = await fetch(`${API}/stac/collections/${collectionId}/items/${itemId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
}

export interface StacItemPatch {
  stac_version?: string | null;
  stac_extensions?: string[] | null;
  geometry?: Geometry | null;
  bbox?: number[] | null;
  properties?: Record<string, unknown> | null;
  links?: Array<Record<string, unknown>> | null;
  assets?: Record<string, unknown> | null;
}

export async function patchStacItem(token: string, collectionId: string, itemId: string, patch: StacItemPatch): Promise<StacItem> {
  const res = await fetch(`${API}/stac/collections/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(itemId)}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export async function deleteAsset(token: string, collectionId: string, itemId: string, assetKey: string): Promise<StacItem> {
  const res = await fetch(
    `${API}/stac/collections/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(itemId)}/assets/${encodeURIComponent(assetKey)}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    const error = new Error(await getErrorMessage(res, `${res.status} ${res.statusText}`)) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  return res.json();
}
